import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { useParams } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";

import {
  INSERT_OFFER_COMMAND,
  REMOVE_OFFER_COMMAND,
} from "./editor/offerCommands";
import type { TextComposerOfferSlice } from "./offerExtension";
import { SectionAccordion } from "../../compose/SectionAccordion";
import type { ExtensionRenderProps } from "../../editor/extensionContract";
import { useMessageDraftStore } from "../../editor/messageDraftStoreFactory";
import type { Offer } from "@/api/offerBank";

import { useOffers } from "@/api/offerBank";
import { DISCOUNT_TYPES } from "@/features/CouponCodes/CouponCodes.types";
import {
  getOfferDiscountCodeLabel,
  getOfferFallbackCode,
  getOfferPreview,
} from "@/grapesPlugins/email/OfferBlock/offerUtils";
import {
  CheckIcon as Check,
  SearchIcon as Search,
  WarningAmberIcon as WarningAmber,
} from "@/icons";
import { OfferCodeIcon } from "@/orion/OrionIcons/OrionOfferCodeIcon";
import { useIsDemoUser } from "@/utils/demoMode";

const ACTIVE_OFFERS_PARAMS = { states: ["active"] as const };

function OffersEmptyState({ siteId }: { siteId: string }) {
  // Coupon codes are redacted for demo accounts — no dead-end link (FOG-2234).
  const isDemoUser = useIsDemoUser();
  return (
    <Box
      sx={(theme) => ({
        p: 4,
        backgroundColor: theme.palette.background["paper-elevation-1"],
        borderRadius: 2,
      })}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <WarningAmber sx={{ fontSize: 16, color: "text.primary", mr: 3 }} />
        <Typography variant="subtitle2" fontWeight={500}>
          No offers yet
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        You don&apos;t have any offers set up. Create one to insert it here.
      </Typography>
      {!isDemoUser && (
        <Button
          variant="contained"
          size="small"
          href={`/settings/${siteId}/coupon-codes`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Create Offer
        </Button>
      )}
    </Box>
  );
}

function OffersTable({
  offers,
  selectedSlug,
  onSelect,
}: {
  offers: Offer[];
  selectedSlug: string | undefined;
  onSelect: (offer: Offer) => void;
}) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Discount name</TableCell>
          <TableCell>Discount Code</TableCell>
          <TableCell>Code Type</TableCell>
          <TableCell>Type</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {offers.map((offer, index) => {
          const isSelected = offer.slug === selectedSlug;
          return (
            <TableRow
              key={offer.slug}
              hover
              selected={isSelected}
              onClick={() => onSelect(offer)}
              sx={(theme) => ({
                cursor: "pointer",
                backgroundColor:
                  index % 2 === 0
                    ? theme.palette.background["paper-elevation-0"]
                    : theme.palette.common.white,
                "&.Mui-selected": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                "&.Mui-selected:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                },
              })}
            >
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {isSelected && <Check fontSize="small" color="primary" />}
                  {offer.name}
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={getOfferDiscountCodeLabel(offer)}
                  size="small"
                  color="default"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={(offer.code_source_type === "static"
                    ? "static"
                    : "unique"
                  ).toLocaleUpperCase()}
                  size="small"
                  color="info"
                />
              </TableCell>
              <TableCell>
                {DISCOUNT_TYPES.find((t) => t.value === offer.type)?.label ||
                  "-"}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export function OffersSection({
  authoringState,
  disabled = false,
}: ExtensionRenderProps) {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const { siteId } = useParams({ strict: false });
  const [editor] = useLexicalComposerContext();

  const setOffer = useMessageDraftStore(
    (s) =>
      s.actions.setOffer as (offer: TextComposerOfferSlice | undefined) => void,
  );

  const { data, isLoading } = useOffers(siteId ?? "", ACTIVE_OFFERS_PARAMS);
  const allOffers = useMemo(() => data?.offers ?? [], [data?.offers]);

  const filteredOffers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allOffers;
    return allOffers.filter(
      (o) =>
        o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q),
    );
  }, [allOffers, search]);

  const selectedSlug = authoringState.offer?.slug;

  const selectedOffer = useMemo(
    () => allOffers.find((o) => o.slug === selectedSlug),
    [allOffers, selectedSlug],
  );

  const selectedOfferChipLabel = useMemo(
    () =>
      selectedOffer ? getOfferDiscountCodeLabel(selectedOffer) : undefined,
    [selectedOffer],
  );

  const handleSelect = useCallback(
    (offer: Offer) => {
      if (offer.slug === selectedSlug) {
        setOffer(undefined);
        editor.update(() => {
          editor.dispatchCommand(REMOVE_OFFER_COMMAND, {
            slug: offer.slug,
          });
        });
      } else {
        const fallbackCode = getOfferFallbackCode(offer);

        setOffer({ slug: offer.slug, fallbackCode });
        editor.focus(() => {
          editor.dispatchCommand(INSERT_OFFER_COMMAND, {
            slug: offer.slug,
            label: getOfferPreview(offer),
            fallbackCode,
          });
        });
      }
    },
    [editor, selectedSlug, setOffer],
  );

  return (
    <SectionAccordion
      title="Offer"
      icon={OfferCodeIcon}
      chips={
        selectedOfferChipLabel ? (
          <Chip label={selectedOfferChipLabel} size="small" color="primary" />
        ) : undefined
      }
      summaryId="text-composer-offers-header"
      detailsId="text-composer-offers-content"
      expanded={expanded}
      onExpandedChange={setExpanded}
      disabled={disabled}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select an offer &mdash; only one can be active in the message at a time
      </Typography>

      <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Preview shows an example code. Static offers display the same code for
          all recipients &ndash; unique offers generate a personalized code per
          recipient.
        </Typography>
      </Alert>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress size={24} />
        </Box>
      ) : allOffers.length === 0 ? (
        <OffersEmptyState siteId={siteId ?? ""} />
      ) : (
        <>
          <TextField
            placeholder="Search offers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            sx={{ mb: 3 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            inputProps={{ "data-1p-ignore": true }}
          />

          {filteredOffers.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 5 }}
            >
              No offers match your search
            </Typography>
          ) : (
            <Box sx={{ overflowY: "auto", maxHeight: 300 }}>
              <OffersTable
                offers={filteredOffers}
                selectedSlug={selectedSlug}
                onSelect={handleSelect}
              />
            </Box>
          )}
        </>
      )}
    </SectionAccordion>
  );
}
